/**
 * Slice 14 / 35 — Keys on arrival: where → code → match refs → proof photo.
 * In-app camera only — no gallery; capture metadata stored for claims.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { readSimpleFlowState } from "@/lib/simpleTurnover";
import {
  appendPhotoCaptureRecord,
  captureFrameFromVideo,
  uploadInAppCleaningPhoto,
} from "@/lib/cleaningPhotoCapture";
import { simpleFlowSegmentIndex } from "@/lib/turnoverWizardPlan";
import { SimpleStepHeader } from "@/components/cleaner/SimpleStepHeader";
import { CleanerMediaLightbox } from "@/components/cleaner/CleanerMediaLightbox";
import { CLEANER_SIMPLE_CTA_FOOTER, CLEANER_SIMPLE_STEP_SHELL } from "@/lib/turnoverChrome";
import { useResolvedStorageUrls } from "@/hooks/useResolvedStorageUrls";

const ENTRY_METHOD_KEYS: Record<string, string> = {
  lockbox: "entryLockbox",
  keypad: "entryKeypad",
  key: "entryPhysicalKey",
  smart_lock: "entrySmartLock",
  concierge: "entryConcierge",
  other: "entryOther",
};

export type KeysAccessHint = {
  entry_location?: string | null;
  entry_method?: string | null;
  entry_code?: string | null;
  /** Extra muted line under method chip (e.g. detail after comma). */
  location_detail?: string | null;
  key_location_reference_urls?: string[] | null;
};

interface Props {
  propertyId: string;
  taskId: string;
  entryWalkthroughState: unknown;
  initialPhotoUrl?: string | null;
  access?: KeysAccessHint | null;
  onComplete: () => void;
  onBack?: () => void;
  onSelectSegment?: (index: number) => void;
  includeMaintenance?: boolean;
  includeQuickCheck?: boolean;
  includeGuestSetup?: boolean;
}

function splitLocation(raw: string | null | undefined): { headline: string; detail: string | null } {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return { headline: "", detail: null };
  const comma = trimmed.indexOf(",");
  if (comma > 0 && comma < 56) {
    return {
      headline: trimmed.slice(0, comma).trim(),
      detail: trimmed.slice(comma + 1).trim() || null,
    };
  }
  return { headline: trimmed, detail: null };
}

export function SimpleKeysStep({
  propertyId,
  taskId,
  entryWalkthroughState,
  initialPhotoUrl,
  access,
  onComplete,
  onBack,
  onSelectSegment,
  includeMaintenance = false,
  includeQuickCheck = false,
  includeGuestSetup = false,
}: Props) {
  const { t, tDynamic } = useTranslation();
  const simple = readSimpleFlowState(entryWalkthroughState);
  const [photoUrl, setPhotoUrl] = useState(
    () => initialPhotoUrl || simple.keys_photo_url || null,
  );
  const [confirmed, setConfirmed] = useState(() => !!photoUrl || !!simple.keys_complete);
  const [mode, setMode] = useState<"idle" | "camera">("idle");
  const [busy, setBusy] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const shutterLock = useRef(false);

  const ready = !!(confirmed && photoUrl);
  const { headline, detail: detailFromLoc } = splitLocation(access?.entry_location);
  const detail = (access?.location_detail ?? detailFromLoc)?.trim() || null;
  const methodKey = access?.entry_method ? ENTRY_METHOD_KEYS[access.entry_method] : null;
  const methodLabel = methodKey
    ? t(methodKey)
    : access?.entry_method || null;
  const code = access?.entry_code?.trim() || null;
  const refs = (access?.key_location_reference_urls ?? []).filter(Boolean).slice(0, 3);
  const displayRefs = useResolvedStorageUrls(refs);
  const hasLocation = !!headline;

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(t("couldNotOpenCamera"));
      return;
    }
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        video.muted = true;
        await video.play().catch(() => undefined);
      }
      setCameraReady(true);
    } catch (err) {
      const name = err instanceof Error ? err.name : "";
      setCameraError(name === "NotAllowedError" ? t("allowCameraAccess") : t("couldNotOpenCamera"));
      setCameraReady(false);
    }
  }, [stopCamera, t]);

  useEffect(() => {
    if (mode !== "camera") {
      stopCamera();
      return;
    }
    void startCamera();
    return () => stopCamera();
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const captureFromVideo = async () => {
    if (shutterLock.current || busy) return;
    const video = videoRef.current;
    if (!video || !cameraReady) {
      toast({
        title: t("cameraRequired"),
        description: t("cameraRequiredDesc"),
        variant: "destructive",
      });
      void startCamera();
      return;
    }
    shutterLock.current = true;
    setBusy(true);
    try {
      const frame = await captureFrameFromVideo(video);
      const { url, record } = await uploadInAppCleaningPhoto({
        propertyId,
        taskId,
        moment: "keys",
        blob: frame.blob,
        width: frame.width,
        height: frame.height,
        storagePath: `closeout_keys/${propertyId}/${taskId}/keys_${Date.now()}.jpg`,
      });
      setPhotoUrl(url);
      setConfirmed(true);
      const prev = readSimpleFlowState(entryWalkthroughState);
      const { error: upErr } = await supabase
        .from("cleaning_tasks")
        .update({
          entry_walkthrough_state: {
            ...prev,
            simple_flow: true,
            keys_photo_url: url,
            photo_captures: appendPhotoCaptureRecord(prev.photo_captures, record),
          },
        })
        .eq("id", taskId);
      if (upErr) throw upErr;
      setMode("idle");
      stopCamera();
    } catch (err) {
      toast({
        title: t("captureFailed"),
        description: err instanceof Error ? err.message : t("tryAgain"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      shutterLock.current = false;
    }
  };

  const handleNext = async () => {
    if (!ready) {
      toast({ title: t("keysHere"), description: t("confirmKeysFirst"), variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const prev = readSimpleFlowState(entryWalkthroughState);
      const { error } = await supabase
        .from("cleaning_tasks")
        .update({
          entry_walkthrough_state: {
            ...prev,
            simple_flow: true,
            keys_complete: true,
            keys_photo_url: photoUrl,
          },
        })
        .eq("id", taskId);
      if (error) throw error;
      onComplete();
    } catch (err) {
      toast({
        title: t("couldNotContinue"),
        description: err instanceof Error ? err.message : t("tryAgain"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={CLEANER_SIMPLE_STEP_SHELL}>
      <SimpleStepHeader
        title={t("keysTitle")}
        activeSegment={simpleFlowSegmentIndex("keys", { includeMaintenance, includeQuickCheck, includeGuestSetup })}
        includeMaintenance={includeMaintenance}
        includeQuickCheck={includeQuickCheck}
        includeGuestSetup={includeGuestSetup}
        onBack={onBack}
        onSelectSegment={onSelectSegment}
      />

      {mode === "camera" ? (
        <div className="flex min-h-0 flex-1 flex-col px-5 pb-6">
          <div className="relative flex min-h-0 flex-1 flex-col justify-end overflow-hidden rounded-[20px] bg-[#1c2732]">
            {hasLocation || code ? (
              <div className="absolute left-3 right-3 top-3 z-[2] rounded-[14px] border border-white/15 bg-black/45 px-3.5 py-2.5 backdrop-blur-sm">
                {hasLocation ? (
                  <div className="text-[15px] font-bold leading-snug text-white">{tDynamic(headline)}</div>
                ) : null}
                {code ? (
                  <div className="mt-1 font-mono text-[18px] font-bold tracking-[2px] text-white/95">{code}</div>
                ) : null}
              </div>
            ) : null}
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              className={cn("absolute inset-0 h-full w-full object-cover", !cameraReady && "opacity-0")}
            />
            {!cameraReady ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
                <Camera className="h-10 w-10 text-white/50" strokeWidth={1.8} />
                <p className="text-[15px] font-medium text-white/70">{cameraError || t("startingCamera")}</p>
                <button
                  type="button"
                  onClick={() => void startCamera()}
                  className="rounded-full bg-[#24a89a] px-5 py-3 text-[15px] font-bold text-white"
                >
                  {t("openCamera")}
                </button>
                <p className="text-[12px] font-medium text-white/45">
                  {t("photosInAppOnly")}
                </p>
              </div>
            ) : null}
            <div className="relative z-[1] flex items-center justify-center px-6 pb-6">
              <button
                type="button"
                disabled={busy}
                onClick={() => void captureFromVideo()}
                aria-label={t("capture")}
                className="flex h-24 w-24 items-center justify-center rounded-full border-[5px] border-white/55 active:scale-95 disabled:opacity-60"
              >
                <span className="flex h-[74px] w-[74px] items-center justify-center rounded-full bg-white">
                  {busy ? <Loader2 className="h-7 w-7 animate-spin text-[#24a89a]" /> : null}
                </span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-5 pb-2">
            <div className="flex flex-col gap-2">
              <div className="text-[11px] font-bold tracking-[0.8px] text-[#15776d]">{t("whereToFindIt")}</div>
              {hasLocation ? (
                <>
                  <div className="text-[27px] font-bold leading-[1.2] tracking-[-0.6px] text-[#0f1720]">
                    {tDynamic(headline)}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {methodLabel ? (
                      <span className="rounded-full bg-[#e4f2f0] px-3 py-1.5 text-[12.5px] font-bold text-[#0d453f]">
                        {methodLabel}
                      </span>
                    ) : null}
                    {detail ? (
                      <span className="text-[13.5px] font-medium text-[#64748b]">{tDynamic(detail)}</span>
                    ) : null}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[17px] font-bold text-[#64748b]">{t("noKeyLocationSet")}</div>
                  <div className="text-[13px] font-medium leading-snug text-[#8494a2]">
                    {t("askHostWhenThere")}
                  </div>
                </>
              )}
            </div>

            {code ? (
              <div className="flex items-center gap-3 rounded-[14px] border-[1.5px] border-[#cfe6e2] bg-white px-[15px] py-3">
                <div className="text-[11px] font-bold tracking-[0.7px] text-[#15776d]">{t("code")}</div>
                <div className="font-mono text-[30px] font-bold tracking-[3px] text-[#0d453f]">{code}</div>
              </div>
            ) : null}

            {refs.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-[11px] font-bold tracking-[0.7px] text-[#8494a2]">{t("matchThis")}</div>
                  <div className="text-[11px] font-medium text-[#8494a2]">{t("tapToEnlarge")}</div>
                </div>
                <div
                  className={cn(
                    "grid gap-2",
                    refs.length === 1 ? "grid-cols-1" : refs.length === 2 ? "grid-cols-2" : "grid-cols-3",
                  )}
                >
                  {refs.map((url, i) => (
                    <button
                      key={url}
                      type="button"
                      onClick={() => {
                        setLightboxIndex(i);
                        setLightboxOpen(true);
                      }}
                      className={cn(
                        "relative w-full overflow-hidden rounded-[14px] border border-[#e6edf1] bg-[#eff4f7] text-left active:opacity-90",
                        refs.length === 1 ? "aspect-[4/3] min-h-[200px]" : "aspect-square min-h-[120px]",
                      )}
                      aria-label={t("tapToEnlarge")}
                    >
                      <img
                        src={displayRefs[i] || url}
                        alt=""
                        className="absolute inset-0 h-full w-full object-contain"
                      />
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div
              className={cn(
                "relative flex min-h-[160px] flex-1 flex-col items-center justify-center gap-3 overflow-hidden rounded-[18px]",
                photoUrl
                  ? "border-[1.5px] border-[#24a89a] bg-[#eff4f7]"
                  : "border-2 border-dashed border-[#b9cbd3] bg-white",
              )}
            >
              {photoUrl ? (
                <img src={photoUrl} alt={t("keysTitle")} className="absolute inset-0 h-full w-full object-cover" />
              ) : (
                <>
                  <Camera className="h-11 w-11 text-[#24a89a]" strokeWidth={1.8} />
                  <div className="text-[15px] font-bold text-[#475569]">{t("photoOfTheKeys")}</div>
                  <div className="text-[12.5px] font-medium text-[#8494a2]">{t("confirmKeysAreHere")}</div>
                </>
              )}
            </div>
          </div>

          <div className={cn(CLEANER_SIMPLE_CTA_FOOTER, "flex flex-col gap-2.5")}>
            <button
              type="button"
              disabled={busy}
              onClick={() => setMode("camera")}
              className={cn(
                "flex h-14 w-full items-center justify-center gap-2.5 rounded-[15px] text-[17px] font-bold",
                ready
                  ? "border-[1.5px] border-[#e6edf1] bg-[#f4f7f9] text-[#475569]"
                  : "bg-[#24a89a] text-white hover:bg-[#1f958a]",
              )}
            >
              {busy ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Camera className="h-5 w-5" strokeWidth={2.4} />
                  {ready ? t("retake") : t("keysHere")}
                </>
              )}
            </button>
            <button
              type="button"
              disabled={busy || !ready}
              onClick={() => void handleNext()}
              className={cn(
                "flex h-[52px] w-full items-center justify-center rounded-[14px] text-[16px] font-bold",
                ready ? "bg-[#24a89a] text-white hover:bg-[#1f958a]" : "bg-[#e8edf0] text-[#a2b0b9]",
              )}
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("next")}
            </button>
          </div>
        </>
      )}

      <CleanerMediaLightbox
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        urls={displayRefs.length === refs.length ? displayRefs : refs}
        initialIndex={lightboxIndex}
      />
    </div>
  );
}
